import { DefaultLanguage } from '~src/i18n/i18n-config';

interface APIAmountArg {
  defaultMFD?: number;
  currency?: string;
  value?: number;
}

export function getCurrencyNumberFormatter({ locale, currency }: { locale: string; currency: string | undefined }) {
  if (!currency) {
    return null;
  }

  return Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });
}

export function getCurrencyConverterUnit({
  currency,
  /**
   * We only need this because the data model can't guarante that
   * there's a currency provided, in the case we don't have it, we
   * default it to "2", because most currencies, like SEK, USD and EUR
   * uses that.
   */
  defaultFractionDigits: defaultValue = 2,
}: {
  currency?: string;
  defaultFractionDigits?: number;
}): number {
  /**
   * The hardcoded locale doens't matter since we only care about
   * the minimum fraction digits, which is only currency-based.
   */
  const formatter = getCurrencyNumberFormatter({ locale: DefaultLanguage.code, currency });

  /**
   * The MDF (minimum fraction digits) determinates the smallest possible
   * unit of a currency, for example SEK = öre, EUR or USD = cents.
   * Where JYP has zero decimal places.
   */
  const mfd = formatter?.resolvedOptions().minimumFractionDigits;

  /**
   * Need to double-check this, since 0 is an actual value in this case.
   */
  if (!mfd && mfd !== 0) {
    return Math.pow(10, defaultValue);
  }

  return Math.pow(10, mfd);
}

export function formatAmount(value: number, currency: string, locale: string = DefaultLanguage.code): string {
  const { minimumFractionDigits, maximumFractionDigits } = getCurrencyNumberFormatter({
    locale,
    currency,
  })!.resolvedOptions();

  return value.toLocaleString(locale ?? DefaultLanguage.code, {
    minimumFractionDigits,
    maximumFractionDigits,
    currency: currency,
    style: 'currency',
  });
}

export function toAmountAPIUpsert({ value, currency, defaultMFD }: APIAmountArg): number | undefined {
  if (value === undefined) {
    return undefined;
  }

  return Math.round(value * getCurrencyConverterUnit({ currency, defaultFractionDigits: defaultMFD }));
}

export function fromAPIAmount({ value, currency, defaultMFD }: APIAmountArg): number | undefined {
  if (value === undefined) {
    return undefined;
  }

  return value / getCurrencyConverterUnit({ currency, defaultFractionDigits: defaultMFD });
}
